import { useState, useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";

function extractTemplateFields(endpoint) {
  return [...endpoint.matchAll(/\{(\w+)\}/g)].map((m) => m[1]);
}

function resolveTemplate(endpoint, depValues) {
  return endpoint.replace(/\{(\w+)\}/g, (_, field) => depValues[field] ?? "");
}

function resolveParams(params, depValues) {
  return Object.fromEntries(
    Object.entries(params ?? {}).map(([key, val]) => [
      key,
      val?.from_field ? (depValues[val.from_field] ?? "") : val,
    ])
  );
}

function resolveLabel(dataItem, labelSpec) {
  if (typeof labelSpec === "string") return dataItem[labelSpec] ?? "";
  if (labelSpec?.concat)
    return labelSpec.concat.map((part) => dataItem[part] ?? part).join("");
  return "";
}

function applyMapResult(data, spec) {
  if (!spec) return data;
  const items = Array.isArray(data) ? data : (data?.data ?? data);
  if (spec.if_empty !== undefined && (!items || items.length === 0))
    return spec.if_empty;
  let result = (items ?? []).map((item) => ({
    ...item,
    key: item[spec.key],
    label: resolveLabel(item, spec.label),
  }));
  if (spec.prepend) result = [...spec.prepend, ...result];
  return result;
}

export function useSelectOptions(item) {
  const { control, setValue } = useFormContext();
  const [options, setOptions] = useState(item.options ?? []);
  const [isLoading, setIsLoading] = useState(false);
  const api = item.get_api;
  // Opt-in per field (auszahlung_schaden_bank_account_id only, see
  // payment_new.js) - every other select must keep requiring an explicit
  // click, so this never runs unless the formdef item asks for it.
  const autoSelectSingleOption = item.auto_select_single_option === true;
  const hasAutoSelectedRef = useRef(false);

  const templateFields = extractTemplateFields(api?.endpoint ?? "");
  const paramFields = Object.values(api?.params ?? {})
    .filter((v) => v?.from_field)
    .map((v) => v.from_field);
  const depFields = [
    ...new Set(
      [item.depends_on, ...templateFields, ...paramFields].filter(Boolean)
    ),
  ];

  const watched = useWatch({ control, name: depFields });
  const depValues = Object.fromEntries(
    depFields.map((f, i) => [f, watched[i]])
  );
  // Only template fields (URL path params like {field}) gate the fetch.
  // Query params (from_field) and depends_on only trigger re-fetch but don't block it.
  const gatingFields = [...new Set([item.depends_on, ...templateFields].filter(Boolean))];
  const enabled = !api || gatingFields.every((f) => !!depValues[f]);

  useEffect(() => {
    if (!api || !enabled) return;
    const controller = new AbortController();
    const resolvedEndpoint = resolveTemplate(api.endpoint, depValues);
    const resolvedParams = resolveParams(api.params, depValues);
    // A new fetch (e.g. the claim's object changed) gets its own fresh chance
    // to auto-select, even if an earlier fetch already did so once.
    hasAutoSelectedRef.current = false;

    backendGet(resolvedEndpoint, {
      setIsLoading,
      options: Object.keys(resolvedParams).length
        ? { params: resolvedParams }
        : null,
      setResult: (data) => {
        const resolvedOptions = applyMapResult(data, api.map_result);
        setOptions(resolvedOptions);

        if (autoSelectSingleOption && !hasAutoSelectedRef.current) {
          // Placeholder entries (e.g. "Individuelle Bankverbindung") use the
          // empty key and must not count as "the one real option".
          const realOptions = resolvedOptions.filter((option) => option.key !== "");
          if (realOptions.length === 1) {
            hasAutoSelectedRef.current = true;
            setValue(item.name, realOptions[0].key, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
        }
      },
      signal: controller.signal,
    });

    return () => controller.abort();
  }, [JSON.stringify(watched), enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  return { options, isLoading };
}
